import { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Input,
  Button,
  Form,
  message,
  Result,
  Typography,
  Row,
  Col,
  Space,
  Alert,
  Progress,
  Tag,
  Badge,
  Statistic,
  Pagination,
  Upload,
} from "antd";
import {
  LeftOutlined,
  RightOutlined,
  CheckOutlined,
  FileTextOutlined,
  CalendarOutlined,
  UserOutlined,
  EditOutlined,
  SendOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  UploadOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import apiClient from "../../services/apiClient";
import { getSampleNewsData, getSampleCountData } from "./sampleNewsData";
import { ThemeContext } from "../../context/ThemeContext";
import "./NewsApproval.css";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const NewsApproval = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state?.data;
  const { isDark } = useContext(ThemeContext);
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [form] = Form.useForm();

  const [newsData, setNewsData] = useState([]);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [isAllApproved, setIsAllApproved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [usingSampleData, setUsingSampleData] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [counts, setCounts] = useState({
    lmCount: 0,
    bsCount: 0,
    feCount: 0,
    etCount: 0,
    mcCount: 0,
    twitterCount: 0
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [newsType, setNewsType] = useState(data == "livemint" ? "live-mint"
    : data == "businessstandard" ? "business-standard"
      : data == "financialexpress" ? "financial-express"
        : data == "economictimes" ? "economic-times" : data, "");



  useEffect(() => {
    fetchNewsData();
    fetchCounts();
  }, [data]);

  const fetchCounts = async () => {
    try {
      const response = await apiClient.get(`${baseUrl}/news/count`);

      if (response && response.data && response.data.data) {
        const response1 = response.data.data;
        setCounts({
          bsCount: response1.businessStandard || 0,
          feCount: response1.financialExpress || 0,
          etCount: response1.economicTimes || 0,
          mcCount: response1.mcCount || 0,
          lmCount: response1.liveMint || 0,
          twitterCount: response1.twitter || 0,
        });
        // message.success("Counts refreshed successfully!");
      }
    } catch (error) {
      console.error("Error fetching counts:", error);

    }
  };
  const fetchNewsData = async (page = 1, pageSize = 10) => {
    setLoading(true);
    setApiError(false);
    try {
      const response = await apiClient.get(`${baseUrl}/news/${newsType}?page=${page - 1}&size=${pageSize}`);

      const result = response?.data?.data;
      if (result?.content?.length > 0) {
        setNewsData(result.content);
        setCurrentNewsIndex(0);
        form.setFieldsValue(result.content[0]);
        setImageUrl(result.content[0].imageUrl || "");
        setUsingSampleData(false);

        setPagination({
          current: page,
          pageSize: pageSize,
          total: response.data.data.totalElements || 0,
        });
      } else {
        setNewsData([]);
      }
    } catch (error) {
      console.error("Error fetching news data:", error);
      setApiError(true);
      setNewsData([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page, pageSize) => {
    fetchNewsData(page, pageSize);
  };

  const onFinish = async (values) => {
    setLoading(true);

    try {
      const updatedNews = {
        ...newsData[currentNewsIndex],
        ...values,
        subtitle: values.subtitle || "",
        imageUrl: imageUrl || "",
        ...{ newsType: data }
      };

      console.log("updated news,...", updatedNews)


      // Only make API call if not using sample data
      await apiClient.post(`${baseUrl}/news/approve`, updatedNews);

      // Refresh counts after successful approval
      fetchCounts();
      // fetchNewsData();

      message.success("News published successfully!");

      // Move to next news item or mark all as approved
      if (currentNewsIndex < newsData.length - 1) {
        const newIndex = currentNewsIndex + 1;
        setCurrentNewsIndex(newIndex);
        form.setFieldsValue(newsData[newIndex]);
        setImageUrl(newsData[newIndex].imageUrl || "");
      } else {
        setIsAllApproved(true);
      }
    } catch (error) {
      console.error("Error publishing news:", error);

    } finally {
      setLoading(false);
    }
  };

  const handleNextNews = () => {
    if (currentNewsIndex < newsData.length - 1) {
      const newIndex = currentNewsIndex + 1;
      setCurrentNewsIndex(newIndex);
      form.setFieldsValue(newsData[newIndex]);
      setImageUrl(newsData[newIndex].imageUrl || "");
    } else {
      setIsAllApproved(true);
    }
  };

  const handlePreviousNews = () => {
    if (currentNewsIndex > 0) {
      const newIndex = currentNewsIndex - 1;
      setCurrentNewsIndex(newIndex);
      form.setFieldsValue(newsData[newIndex]);
      setImageUrl(newsData[newIndex].imageUrl || "");
    }
  };

  const handleRejectNews = async () => {
    const formData = form.getFieldsValue();

    try {
      const response = await apiClient.delete(`${baseUrl}/news/${newsType}`, {
        data: {
          title: formData.title,
          company: formData.company,
        },
      });

      if (response.data?.statusCode === 200) {
        message.success(response.data.message || "News rejected successfully.");
        fetchCounts();
        fetchNewsData();// Optional: move to next item
      } else {
        message.error("Failed to reject news.");
      }
    } catch (error) {
      message.error(
        error.response?.data?.message || "An error occurred while rejecting news."
      );
      console.error("Error:", error);
    }
  };

  const handleImageUpload = (info) => {
    if (info.file.status === 'uploading') {
      setUploading(true);
      return;
    }
    if (info.file.status === 'done') {
      setUploading(false);
      setImageUrl(info.file.response?.url || URL.createObjectURL(info.file.originFileObj));
      setImageFile(info.file.originFileObj);
      message.success(`${info.file.name} uploaded successfully`);
    }
    if (info.file.status === 'error') {
      setUploading(false);
      message.error(`${info.file.name} upload failed.`);
    }
  };

  const handleImageRemove = () => {
    setImageUrl("");
    setImageFile(null);
    message.success("Image removed successfully");
  };

  const uploadProps = {
    name: 'file',
    action: `${baseUrl}/upload/image`,
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('Image must be smaller than 5MB!');
        return false;
      }
      return true;
    },
    onChange: handleImageUpload,
    onRemove: handleImageRemove,
    showUploadList: false,
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const getSource = () => {
    switch (data) {
      case "livemint":
        return "LiveMint News";
      case "financialexpress":
        return "Financial Express News";
      case "businessstandard":
        return "Business Standard News";
      case "economictimes":
        return "Economic Times News";
      case "moneycontrol":
        return "Money Control News";
      default:
        return "Unknown Source";
    }
  };

  const getSourceColor = () => {
    switch (data) {
      case "mint":
        return "#1890ff";
      case "financial":
        return "#722ed1";
      case "bs":
        return "#52c41a";
      case "economics":
        return "#fa8c16";
      case "moneycontrol":
        return "#eb2f96";
      default:
        return "#666";
    }
  };

  const getProgressPercentage = () => {
    return ((currentNewsIndex + 1) / newsData.length) * 100;
  };

  const getCurrentSourceCount = () => {
    switch (data) {
      case "mint":
        return counts.lmCount;
      case "financial":
        return counts.feCount;
      case "bs":
        return counts.bsCount;
      case "economics":
        return counts.etCount;
      case "moneycontrol":
        return counts.mcCount;
      default:
        return 0;
    }
  };

  const getTotalPendingCount = () => {
    return Object.values(counts).reduce((total, count) => total + count, 0);
  };

  const getStatusColor = (count) => {
    if (count === 0) return "#52c41a"; // Green for no pending
    if (count < 10) return "#faad14"; // Yellow for low count
    return "#ff4d4f"; // Red for high count
  };

  // Show completion message
  if (isAllApproved || newsData.length === 0) {
    return (
      <div className="news-approval-container">
        <div className="news-approval-header">
          <div className="news-approval-header-content">
            <div className="news-approval-header-left">
              <Button
                type="primary"
                shape="round"
                icon={<LeftOutlined />}
                onClick={handleBackClick}
                className="news-approval-back-button"
              >
                Go Back
              </Button>
              <Title level={1} className="news-approval-title">
                <CheckOutlined /> News Approval Complete
              </Title>
              <Text className="news-approval-subtitle">
                All news articles have been reviewed and approved
              </Text>
            </div>
          </div>
        </div>

        <div className="news-approval-content">
          <Result
            status="success"
            title="All news has been approved!"
            subTitle={`Successfully reviewed ${newsData.length
              } articles from ${getSource()}`}
            extra={[
              <Button type="primary" key="back" onClick={handleBackClick}>
                Back to Approval Dashboard
              </Button>,
              <Button key="refresh" onClick={fetchNewsData}>
                Refresh Data
              </Button>,
            ]}
          />
        </div>
      </div>
    );
  }

  const currentNews = newsData[currentNewsIndex];

  return (
    <div className="news-approval-container">
      {/* Header */}
      <div className="news-approval-header">
        <div className="news-approval-header-content">
          <div className="news-approval-header-left">
            <Button
              type="primary"
              shape="round"
              icon={<LeftOutlined />}
              onClick={handleBackClick}
              className="news-approval-back-button"
            >
              Go Back
            </Button>
            <Title level={1} className="news-approval-title">
              <FileTextOutlined /> News Approval
            </Title>
            <Text className="news-approval-subtitle">
              Review and approve news articles from {getSource()}
            </Text>
          </div>
          <div className="news-approval-header-right">
            <div className="news-approval-counts">
              <Badge
                count={getCurrentSourceCount()}
                style={{
                  backgroundColor: getStatusColor(getCurrentSourceCount()),
                  color: "white",
                }}
              >
                <Tag
                  color={getSourceColor()}
                  className="news-approval-source-tag"
                >
                  {getSource()}
                </Tag>
              </Badge>
            </div>
            {usingSampleData && (
              <Tag color="orange" icon={<ClockCircleOutlined />}>
                Sample Data Mode
              </Tag>
            )}
          </div>
        </div>
      </div>

      <div className="news-approval-content">
        {/* Count Summary */}
        <Card className="news-approval-counts-card">
          <div className="news-approval-counts-header">
            <Title level={4} className="news-approval-counts-title">
              <ExclamationCircleOutlined /> Pending Approvals Overview
            </Title>
            <Button
              type="primary"
              icon={<ClockCircleOutlined />}
              onClick={fetchCounts}
              loading={loading}
              size="small"
            >
              Refresh Counts
            </Button>
          </div>

          <div className="news-approval-stats-container">
            {/* Total Pending - Featured Card */}
            <div className="news-approval-total-card">
              <div className="news-approval-total-content">
                <div className="news-approval-total-icon">
                  <ExclamationCircleOutlined />
                </div>
                <div className="news-approval-total-info">
                  <div className="news-approval-total-value">
                    {getTotalPendingCount()}
                  </div>
                  <div className="news-approval-total-label">Total Pending</div>
                </div>
                {/* <div className="news-approval-total-badge">
                  <Badge 
                    count={getTotalPendingCount()} 
                    style={{ 
                      backgroundColor: getStatusColor(getTotalPendingCount()),
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  />
                </div> */}
              </div>
            </div>

            {/* Individual Source Cards */}
            <div className="news-approval-sources-grid">
              <div
                className="news-approval-source-card"
                style={{ borderLeft: "4px solid #1890ff" }}
              >
                <div className="news-approval-source-header">
                  <div
                    className="news-approval-source-icon"
                    style={{ backgroundColor: "#e6f7ff" }}
                  >
                    <FileTextOutlined style={{ color: "#1890ff" }} />
                  </div>
                  <div className="news-approval-source-status">
                    {counts.lmCount > 0 ? (
                      <Tag color="processing" size="small">
                        Pending
                      </Tag>
                    ) : (
                      <Tag color="success" size="small">
                        Clear
                      </Tag>
                    )}
                  </div>
                </div>
                <div className="news-approval-source-content">
                  <div className="news-approval-source-value">
                    {counts.lmCount}
                  </div>
                  <div className="news-approval-source-title">LiveMint</div>
                </div>
              </div>

              <div
                className="news-approval-source-card"
                style={{ borderLeft: "4px solid #52c41a" }}
              >
                <div className="news-approval-source-header">
                  <div
                    className="news-approval-source-icon"
                    style={{ backgroundColor: "#f6ffed" }}
                  >
                    <FileTextOutlined style={{ color: "#52c41a" }} />
                  </div>
                  <div className="news-approval-source-status">
                    {counts.bsCount > 0 ? (
                      <Tag color="processing" size="small">
                        Pending
                      </Tag>
                    ) : (
                      <Tag color="success" size="small">
                        Clear
                      </Tag>
                    )}
                  </div>
                </div>
                <div className="news-approval-source-content">
                  <div className="news-approval-source-value">
                    {counts.bsCount}
                  </div>
                  <div className="news-approval-source-title">
                    Business Standard
                  </div>
                </div>
              </div>

              <div
                className="news-approval-source-card"
                style={{ borderLeft: "4px solid #722ed1" }}
              >
                <div className="news-approval-source-header">
                  <div
                    className="news-approval-source-icon"
                    style={{ backgroundColor: "#f9f0ff" }}
                  >
                    <FileTextOutlined style={{ color: "#722ed1" }} />
                  </div>
                  <div className="news-approval-source-status">
                    {counts.feCount > 0 ? (
                      <Tag color="processing" size="small">
                        Pending
                      </Tag>
                    ) : (
                      <Tag color="success" size="small">
                        Clear
                      </Tag>
                    )}
                  </div>
                </div>
                <div className="news-approval-source-content">
                  <div className="news-approval-source-value">
                    {counts.feCount}
                  </div>
                  <div className="news-approval-source-title">
                    Financial Express
                  </div>
                </div>
              </div>

              <div
                className="news-approval-source-card"
                style={{ borderLeft: "4px solid #fa8c16" }}
              >
                <div className="news-approval-source-header">
                  <div
                    className="news-approval-source-icon"
                    style={{ backgroundColor: "#fff7e6" }}
                  >
                    <FileTextOutlined style={{ color: "#fa8c16" }} />
                  </div>
                  <div className="news-approval-source-status">
                    {counts.etCount > 0 ? (
                      <Tag color="processing" size="small">
                        Pending
                      </Tag>
                    ) : (
                      <Tag color="success" size="small">
                        Clear
                      </Tag>
                    )}
                  </div>
                </div>
                <div className="news-approval-source-content">
                  <div className="news-approval-source-value">
                    {counts.etCount}
                  </div>
                  <div className="news-approval-source-title">
                    Economic Times
                  </div>
                </div>
              </div>

              <div
                className="news-approval-source-card"
                style={{ borderLeft: "4px solid #eb2f96" }}
              >
                <div className="news-approval-source-header">
                  <div
                    className="news-approval-source-icon"
                    style={{ backgroundColor: "#fff0f6" }}
                  >
                    <FileTextOutlined style={{ color: "#eb2f96" }} />
                  </div>
                  <div className="news-approval-source-status">
                    {counts.mcCount > 0 ? (
                      <Tag color="processing" size="small">
                        Pending
                      </Tag>
                    ) : (
                      <Tag color="success" size="small">
                        Clear
                      </Tag>
                    )}
                  </div>
                </div>
                <div className="news-approval-source-content">
                  <div className="news-approval-source-value">
                    {counts.mcCount}
                  </div>
                  <div className="news-approval-source-title">
                    Money Control
                  </div>
                </div>
              </div>
              <div
                className="news-approval-source-card"
                style={{ borderLeft: "4px solid #1890ff" }}
              >
                <div className="news-approval-source-header">
                  <div
                    className="news-approval-source-icon"
                    style={{ backgroundColor: "#e6f7ff" }}
                  >
                    <FileTextOutlined style={{ color: "#1890ff" }} />
                  </div>
                  <div className="news-approval-source-status">
                    {counts.twitterCount > 0 ? (
                      <Tag color="processing" size="small">
                        Pending
                      </Tag>
                    ) : (
                      <Tag color="success" size="small">
                        Clear
                      </Tag>
                    )}
                  </div>
                </div>
                <div className="news-approval-source-content">
                  <div className="news-approval-source-value">
                    {counts.twitterCount}
                  </div>
                  <div className="news-approval-source-title">Twitter</div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Progress and Navigation */}
        {/* <Card className="news-approval-progress-card">
          <div className="news-approval-progress-header">
            <div className="news-approval-progress-info">
              <Title level={4} className="news-approval-progress-title">
                Progress: {currentNewsIndex + 1} of {newsData.length}
              </Title>
              <Progress
                percent={getProgressPercentage()}
                status="active"
                strokeColor={getSourceColor()}
                className="news-approval-progress-bar"
              />
            </div>
            <div className="news-approval-navigation">
              <Button
                icon={<LeftOutlined />}
                onClick={handlePreviousNews}
                disabled={currentNewsIndex === 0}
                className="news-approval-nav-button"
              >
                Previous
              </Button>
              <Button
                icon={<RightOutlined />}
                onClick={handleNextNews}
                disabled={currentNewsIndex === newsData.length - 1}
                className="news-approval-nav-button"
              >
                Next
              </Button>
            </div>
          </div>
        </Card> */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 24,
          }}
        >
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} items`
            }
            onChange={(page, pageSize) => fetchNewsData(page, pageSize)}
          />
        </div>

        {/* News Content */}
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Card className="news-approval-preview-card">
              <div className="news-approval-preview-header">
                <Title level={3} className="news-approval-preview-title">
                  <EyeOutlined /> Article Preview
                </Title>
                <div className="news-approval-meta">
                  <Space>
                    <Tag icon={<CalendarOutlined />}>
                      {currentNews.publishedDate || "Today"}
                    </Tag>
                    <Tag icon={<UserOutlined />}>
                      {currentNews.name || currentNews.company}
                    </Tag>
                  </Space>
                </div>
              </div>

              <div className="news-approval-preview-content">
                <Title level={4} className="news-approval-original-title">
                  {currentNews.title}
                </Title>
                {currentNews.subtitle && (
                  <Paragraph className="news-approval-original-subtitle">
                    {currentNews.subtitle}
                  </Paragraph>
                )}
                {imageUrl && (
                  <div className="news-approval-preview-image-container">
                    <img
                      src={imageUrl}
                      alt="News preview"
                      className="news-approval-preview-content-image"
                    />
                  </div>
                )}
                <Paragraph className="news-approval-original-content">
                  {currentNews.description}
                </Paragraph>
                {currentNews.url && (
                  <div className="news-approval-source-link">
                    <Text type="secondary">
                      Source:{" "}
                      <a
                        href={currentNews.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {currentNews.source}
                      </a>
                    </Text>
                  </div>
                )}
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card className="news-approval-edit-card">
              <div className="news-approval-edit-header">
                <Title level={3} className="news-approval-edit-title">
                  <EditOutlined /> Edit & Approve
                </Title>
              </div>

              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                className="news-approval-form"
              >
                <Form.Item
                  label="Company Name"
                  name="company"
                  className="news-approval-form-item"
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Enter company name"
                    className="news-approval-input"
                    disabled={true}
                  />
                </Form.Item>

                <Form.Item
                  label="News Title"
                  name="title"
                  rules={[{ required: true, message: "Title is required" }]}
                  className="news-approval-form-item"
                >
                  <Input
                    prefix={<FileTextOutlined />}
                    placeholder="Enter news title"
                    className="news-approval-input"
                    disabled={true}
                  />
                </Form.Item>

                <Form.Item
                  label="Subtitle"
                  name="subtitle"
                  className="news-approval-form-item"
                >
                  <TextArea
                    rows={3}
                    placeholder="Enter news subtitle (optional)"
                    className="news-approval-textarea"
                    maxLength={200}
                    showCount
                  />
                </Form.Item>

                <Form.Item
                  label="News Image"
                  className="news-approval-form-item"
                >
                  <div className="news-approval-image-section">
                    {imageUrl && (
                      <div className="news-approval-image-preview">
                        <img
                          src={imageUrl}
                          alt="News preview"
                          className="news-approval-preview-image"
                        />
                        <Button
                          type="text"
                          danger
                          size="small"
                          onClick={handleImageRemove}
                          className="news-approval-remove-image"
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                    <Upload {...uploadProps}>
                      <Button
                        icon={<UploadOutlined />}
                        loading={uploading}
                        className="news-approval-upload-button"
                      >
                        {imageUrl ? "Change Image" : "Upload Image"}
                      </Button>
                    </Upload>
                    <Text
                      type="secondary"
                      className="news-approval-upload-hint"
                    >
                      Supported formats: JPG, PNG, GIF. Max size: 5MB
                    </Text>
                  </div>
                </Form.Item>

                <Form.Item
                  label="News Content"
                  name="description"
                  rules={[{ required: true, message: "Content is required" }]}
                  className="news-approval-form-item"
                >
                  <TextArea
                    rows={12}
                    placeholder="Enter news content"
                    className="news-approval-textarea"
                  />
                </Form.Item>

                <Form.Item className="news-approval-actions">
                  <Space size="middle" className="news-approval-action-buttons">
                    <Button
                      type="default"
                      icon={<LeftOutlined />}
                      onClick={handlePreviousNews}
                      disabled={currentNewsIndex === 0}
                      className="news-approval-action-button"
                    >
                      Previous
                    </Button>
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      htmlType="submit"
                      loading={loading}
                      className="news-approval-action-button"
                      style={{
                        backgroundColor: getSourceColor(),
                        borderColor: getSourceColor(),
                      }}
                    >
                      Approve & Publish
                    </Button>
                    <Button
                      type="default"
                      icon={<RightOutlined />}
                      onClick={handleNextNews}
                      disabled={currentNewsIndex === newsData.length - 1}
                      className="news-approval-action-button"
                    >
                      Next
                    </Button>
                    <Button
                      style={{
                        backgroundColor: "#ff4d4f",
                        color: "white",
                        borderColor: "#ff4d4f",
                      }}
                      icon={<RightOutlined />}
                      onClick={handleRejectNews}
                      className="news-approval-action-button"
                    >
                      Reject
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default NewsApproval;
